(() => {
  if (!window.fetch || !window.AbortController || window.Shopify?.designMode) return;
  let controller;
  let revision = 0;
  const sectionSelector = '[data-shopping-section]';
  const currentSection = () => document.querySelector(sectionSelector);
  const initial = currentSection();
  if (!initial) return;
  const kind = initial.dataset.shoppingKind;
  history.replaceState({ ...history.state, myraaShopping: kind }, '', location.href);

  function sameView(url, section) {
    return url.origin === location.origin && url.pathname === location.pathname && !url.searchParams.has('view') && section?.dataset.shoppingKind === kind;
  }

  async function navigate(url, { variant = false, focusId, push = true, scroll = false } = {}) {
    const section = currentSection();
    if (!sameView(url, section)) return false;
    controller?.abort();
    controller = new AbortController();
    const activeController = controller;
    const signal = controller.signal;
    const request = ++revision;
    const timeout = setTimeout(() => activeController.abort(), 15000);
    const disabled = [];
    let rendered = false;
    section.setAttribute('aria-busy', 'true');
    if (variant) {
      section.dataset.shoppingBusy = 'true';
      section.querySelectorAll('.commerce-product-layout button[type="submit"], .commerce-product-layout [data-cart-quantity] button, .commerce-product-layout [data-cart-quantity] input, .commerce-mobile-purchase button[type="submit"]').forEach(button => {
        if (!button.disabled) { button.disabled = true; disabled.push(button); }
      });
    }
    try {
      const resource = new URL(url);
      resource.searchParams.set('section_id', section.dataset.shoppingSection);
      const response = await fetch(resource, { credentials: 'same-origin', signal, headers: { Accept: 'text/html' } });
      if (!response.ok) throw new Error('Section unavailable');
      const html = await response.text();
      if (request !== revision) return true;
      let replacement = new DOMParser().parseFromString(html, 'text/html').querySelector(sectionSelector);
      if (!replacement || replacement.dataset.shoppingKind !== kind) throw new Error('Section unavailable');
      if (variant) {
        section.querySelectorAll('[name="quantity"], [name^="properties["], [data-delivery-enquiry] input').forEach(input => {
          const next = input.name ? Array.from(replacement.querySelectorAll('input, textarea, select')).find(node => node.name === input.name) : replacement.querySelector('[data-delivery-enquiry] input');
          if (next) next.value = input.value;
        });
        section.querySelectorAll('details[id], .commerce-gift-message').forEach(details => {
          const next = details.id ? replacement.querySelector('#' + CSS.escape(details.id)) : replacement.querySelector('.commerce-gift-message');
          if (next) next.open = details.open;
        });
        section.querySelectorAll('video').forEach(video => video.pause());
        const layout = replacement.querySelector('.commerce-product-layout');
        const specifications = replacement.querySelector('.commerce-product-specifications');
        if (!layout || !section.querySelector('.commerce-product-layout')) throw new Error('Product unavailable');
        section.querySelector('.commerce-product-layout').replaceWith(layout);
        if (specifications) section.querySelector('.commerce-product-specifications')?.replaceWith(specifications);
        replacement = section;
      } else {
        const open = section.querySelector('[data-filter-toggle]')?.getAttribute('aria-expanded');
        const toggle = replacement.querySelector('[data-filter-toggle]');
        const panel = replacement.querySelector('.commerce-filter-panel');
        if (open != null && toggle && panel) {
          toggle.setAttribute('aria-expanded', open);
          panel.classList.toggle('is-open', open === 'true');
        }
      }
      if (!variant) section.replaceWith(replacement);
      rendered = true;
      replacement.removeAttribute('aria-busy');
      delete replacement.dataset.shoppingBusy;
      if (push) history.pushState({ ...history.state, myraaShopping: kind }, '', url);
      document.dispatchEvent(new CustomEvent('shopping:section:updated', { detail: { section: replacement } }));
      if (focusId) replacement.querySelector('#' + CSS.escape(focusId))?.focus({ preventScroll: true });
      if (scroll) replacement.querySelector('.commerce-listing-toolbar')?.scrollIntoView({ block: 'start' });
      const live = document.querySelector('[data-shopping-announcement]') || document.body.appendChild(Object.assign(document.createElement('p'), { className: 'cart-announcement' }));
      live.dataset.shoppingAnnouncement = '';
      live.setAttribute('role', 'status');
      live.textContent = replacement.dataset.shoppingUpdated;
    } catch (_) {
      if (request === revision) location.assign(url);
    } finally {
      clearTimeout(timeout);
      if (request === revision && (!variant || rendered)) {
        section.removeAttribute('aria-busy');
        delete section.dataset.shoppingBusy;
        disabled.forEach(button => { if (!button.hasAttribute('data-cart-busy')) button.disabled = false; });
      }
    }
    return true;
  }

  document.addEventListener('shopping:variant', event => {
    const url = new URL(event.detail.url);
    if (!sameView(url, currentSection())) return;
    if (currentSection().querySelector('.commerce-dynamic-checkout')) return;
    event.preventDefault();
    navigate(url, { variant: true, focusId: event.detail.optionId });
  });
  document.addEventListener('submit', event => {
    if (event.defaultPrevented || !event.target.matches('.commerce-facets, .commerce-search-form')) return;
    const form = event.target;
    const url = new URL(form.action, location.origin);
    if (!sameView(url, currentSection())) return;
    event.preventDefault();
    const params = new URLSearchParams();
    for (const [name, value] of new FormData(form)) if (String(value).trim() !== '') params.append(name, value);
    url.search = params.toString();
    navigate(url, { focusId: document.activeElement?.id });
  });
  document.addEventListener('click', event => {
    if (event.defaultPrevented || event.button !== 0 || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
    const link = event.target.closest('.commerce-pagination a, .commerce-active-filters a, .commerce-facets-heading a');
    if (!link || link.target || link.hasAttribute('download')) return;
    const url = new URL(link.href);
    if (!sameView(url, currentSection())) return;
    event.preventDefault();
    navigate(url, { scroll: Boolean(link.closest('.commerce-pagination')) });
  });
  window.addEventListener('popstate', event => {
    if (kind === 'product' && currentSection()?.querySelector('.commerce-dynamic-checkout')) location.reload();
    else if (event.state?.myraaShopping === kind) navigate(new URL(location.href), { variant: kind === 'product', push: false });
    else location.reload();
  });
})();
